import React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '../dialog/Dialog'
import {
  PricingModelBasicInfoFormValues,
  PricingModelBasicInfoStep,
} from './PricingModelBasicInfoStep'
import { Wizard } from '../form/Wizard'
import {
  ExternalPricingModelEnergyPriceFormValues,
  InternalPricingModelEnergyPriceFormValues,
  PricingModelEnergyPriceStep,
  PricingModelEnergyPriceStepProps,
} from './PricingModelEnergyPriceStep'
import {
  CreatePricingModelInput,
  PricingModelFragment,
  PricingModelPriceData,
  PurchasedEnergyType,
  UpdatePricingModelInput,
} from '../../generated/graphql'
import {
  PricingModelEnergyPriceIncreaseRateFormValues,
  PricingModelEnergyPriceIncreaseRateStep,
} from './PricingModelEnergyPriceIncreaseRateStep'
import {
  formatPercent,
  formatPercentWithPrecision,
} from '../../util/formatters'
import { parseDecimal, parsePercentToDecimal } from '../../util/parsers'
import { EnergyType } from '../../calculators/EnergyFlow'

const purchasedEnergyTypes = Object.values(PurchasedEnergyType)

const defaultPricingModelValues = {
  prices: purchasedEnergyTypes.map((energyType) => ({
    energyType,
    pricesPerMwh: Array<string>(12).fill('0'),
  })),
  sellingPrices: [PurchasedEnergyType.Electricity].map((energyType) => ({
    energyType,
    pricesPerMwh: Array<string>(12).fill('0'),
  })),
  priceIncreaseRates: purchasedEnergyTypes.map((energyType) => ({
    energyType,
    priceIncreaseRate: formatPercent(0.05, { omitUnit: true }),
  })),
}

export enum PricingModelStepType {
  BasicInfo = 'BasicInfo',
  EnergyPrice = 'EnergyPrice',
  EnergySellingPrice = 'EnergySellingPrice',
  EnergyPriceIncreaseRate = 'EnergyPriceIncreaseRate',
}

export type PricingModelFormValues = PricingModelBasicInfoFormValues &
  ExternalPricingModelEnergyPriceFormValues &
  PricingModelEnergyPriceIncreaseRateFormValues

type Props = {
  isOpen: boolean
  isCreate?: boolean
  defaultValues: Partial<PricingModelFormValues>
  stepType?: PricingModelStepType | null
  onSubmit: (values: PricingModelFormValues) => void
  onClose: () => void
}

export const PricingModelDialog = React.memo(
  ({
    isOpen,
    isCreate,
    defaultValues,
    stepType,
    onClose,
    onSubmit,
  }: Props): React.ReactElement => {
    const { t } = useTranslation()

    const defaultPriceEnergyTypes =
      defaultValues.prices?.map((price) => price.energyType) ??
      purchasedEnergyTypes

    const energyPriceSteps = defaultPriceEnergyTypes.map(
      (energyType, index) => (props: PricingModelEnergyPriceStepProps) =>
        PricingModelEnergyPriceStep({
          ...props,
          title: `${t('pricingModels.energyPrices')} | ${t(
            `purchasedEnergyType.${energyType}`,
          )}`,
          arrayName: 'prices',
          arrayIndex: index,
        }),
    )

    const defaultSellingPriceEnergyTypes = defaultValues.sellingPrices?.map(
      (price) => price.energyType,
    ) ?? [PurchasedEnergyType.Electricity]

    const energySellingPriceSteps = defaultSellingPriceEnergyTypes.map(
      (_, index) => (props: PricingModelEnergyPriceStepProps) =>
        PricingModelEnergyPriceStep({
          ...props,
          title: `${t('pricingModels.energySellingPrices')} | ${t(
            `energyType.${EnergyType.Electricity}`,
          )}`,
          arrayName: 'sellingPrices',
          arrayIndex: index,
        }),
    )

    const getSteps = () => {
      switch (stepType) {
        case PricingModelStepType.BasicInfo:
          return [PricingModelBasicInfoStep]
        case PricingModelStepType.EnergyPrice:
          return energyPriceSteps
        case PricingModelStepType.EnergySellingPrice:
          return energySellingPriceSteps
        case PricingModelStepType.EnergyPriceIncreaseRate:
          return [PricingModelEnergyPriceIncreaseRateStep]
        default:
          return [
            PricingModelBasicInfoStep,
            ...energyPriceSteps,
            ...energySellingPriceSteps,
            PricingModelEnergyPriceIncreaseRateStep,
          ]
      }
    }
    const steps = getSteps()

    const title = isCreate ? t('pricingModels.create') : defaultValues?.name

    const mergeArray = (
      oldState: Partial<PricingModelFormValues>,
      additionalState: Partial<PricingModelFormValues>,
      arrayName: 'prices' | 'sellingPrices',
    ) => {
      const updatedPrices = oldState[arrayName] ?? []
      const newPrices = additionalState[arrayName] ?? []

      newPrices.forEach((newPrice, index) => {
        if (newPrice) {
          updatedPrices[index] = newPrice
        }
      })

      return updatedPrices
    }

    const onMerge = (
      formState: Partial<PricingModelFormValues>,
      values: Partial<PricingModelFormValues>,
    ) => {
      const prices = mergeArray(formState, values, 'prices')
      const sellingPrices = mergeArray(formState, values, 'sellingPrices')
      return {
        ...formState,
        ...values,
        prices,
        sellingPrices,
      }
    }

    return (
      <Dialog title={title ?? ''} open={isOpen} onClose={onClose}>
        <Wizard
          steps={steps}
          defaultValues={defaultValues}
          submitText={
            isCreate ? t('pricingModels.create') : t('common.saveChanges')
          }
          onSubmit={onSubmit}
          onMerge={onMerge}
        />
      </Dialog>
    )
  },
  (prevProps, nextProps) => prevProps.isOpen === nextProps.isOpen,
)

export function mapPricingModelToValues(
  pricingModel: PricingModelFragment | null,
): Partial<PricingModelFormValues> {
  const mapPriceToValue = (
    price: Pick<PricingModelPriceData, 'energyType' | 'monthlyValues'>,
  ) => ({
    energyType: price.energyType,
    pricesPerMwh: price.monthlyValues?.map((value) => String(value)) ?? [],
  })

  return pricingModel
    ? {
        name: pricingModel.name,
        buildingYear: String(pricingModel.buildingYear),
        inspectionLengthInYears: String(pricingModel.inspectionLengthInYears),
        yearlyDiscountRate: formatPercentWithPrecision(
          pricingModel.yearlyDiscountRate,
          {
            omitUnit: true,
          },
        ),
        yearlyMaintenanceIncreaseRate: formatPercentWithPrecision(
          pricingModel.yearlyMaintenanceIncreaseRate,
          { omitUnit: true },
        ),
        prices: pricingModel.prices.map(mapPriceToValue),
        sellingPrices: pricingModel.sellingPrices.map(mapPriceToValue),
        priceIncreaseRates: pricingModel.priceIncreaseRates.map(
          (priceIncreaseRate) => ({
            energyType: priceIncreaseRate.energyType,
            priceIncreaseRate: formatPercentWithPrecision(
              priceIncreaseRate.yearlyValue ?? 0.05,
              {
                omitUnit: true,
              },
            ),
          }),
        ),
      }
    : defaultPricingModelValues
}

export function mapFormValuesToInput<
  TCreatePricingModelInput extends CreatePricingModelInput,
>(values: PricingModelFormValues): TCreatePricingModelInput

export function mapFormValuesToInput<
  TUpdatePricingModelInput extends UpdatePricingModelInput,
>(values: PricingModelFormValues): TUpdatePricingModelInput
export function mapFormValuesToInput(
  values: PricingModelFormValues,
): CreatePricingModelInput | UpdatePricingModelInput {
  const mapPriceToInput = (
    price: InternalPricingModelEnergyPriceFormValues,
  ) => ({
    energyType: price.energyType,
    monthlyValues: price.pricesPerMwh.map((pricePerMwh) =>
      parseDecimal(pricePerMwh),
    ),
  })

  return {
    name: values.name,
    buildingYear: parseDecimal(values.buildingYear),
    inspectionLengthInYears: parseDecimal(values.inspectionLengthInYears),
    yearlyDiscountRate: parsePercentToDecimal(values.yearlyDiscountRate),
    yearlyMaintenanceIncreaseRate: parsePercentToDecimal(
      values.yearlyMaintenanceIncreaseRate,
    ),
    prices: values.prices.map(mapPriceToInput),
    sellingPrices: values.sellingPrices.map(mapPriceToInput),
    priceIncreaseRates: values.priceIncreaseRates.map((priceIncreaseRate) => ({
      energyType: priceIncreaseRate.energyType,
      yearlyValue: parsePercentToDecimal(priceIncreaseRate.priceIncreaseRate),
    })),
  }
}
